/* eslint no-param-reassign: "error" */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { requestStatusType, Member } from '../../../types';
import { TeamSpaceUserRoleType } from '../../../constants/companySharedConstants';
import { FolderItem } from '../../../types/state';
import { inviteToTeamOrFolder } from '../../../services/teamService';
import { RootState } from '../../../reducers';

interface Invitation {
  members: Member[];
  // users to invite
  newUserEmails: string[];
  role: TeamSpaceUserRoleType;
  folders: FolderItem[];
}

export interface InviteState {
  status: requestStatusType;
  invitation: Invitation;
}

const initialState: InviteState = {
  status: 'idle',
  invitation: {
    members: [],
    newUserEmails: [],
    role: 'Member',
    folders: [],
  },
};

export const sendInvitations = createAsyncThunk<
  void,
  void,
  { state: RootState }
>('invite/sendInvitations', async (_, { getState }) => {
  const { invite, team } = getState();
  const { members, role, newUserEmails } = invite.invitation;
  const emails = members.map((member) => member.email).concat(newUserEmails);
  const { teamId } = team.selectedTeam || {};

  if (teamId) {
    await inviteToTeamOrFolder({
      teamId,
      folderIds: [],
      emails,
      role,
    });
  }
});

const inviteSlice = createSlice({
  name: 'invite',
  initialState,
  reducers: {
    setMembersAndNewUserEmails: (
      state,
      action: PayloadAction<{
        members: Member[];
        newUserEmails: string[];
      }>
    ) => {
      state.invitation.members = action.payload.members;
      state.invitation.newUserEmails = action.payload.newUserEmails;
    },
    setRole: (state, action: PayloadAction<TeamSpaceUserRoleType>) => {
      state.invitation.role = action.payload;
    },
    setFolders: (state, action: PayloadAction<FolderItem[]>) => {
      state.invitation.folders = action.payload;
    },
    reset: (state) => {
      state.status = 'idle';
      state.invitation = initialState.invitation;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendInvitations.fulfilled, (state) => {
      state.invitation = initialState.invitation;
      state.status = 'succeeded';
    });
    builder.addCase(sendInvitations.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(sendInvitations.rejected, (state) => {
      state.status = 'failed';
    });
  },
});

export const {
  setMembersAndNewUserEmails,
  setRole,
  setFolders,
  reset,
} = inviteSlice.actions;
export default inviteSlice.reducer;
